import { useState } from 'react';
import { Plus, Receipt, Search } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useAccountsQuery, useCreateSaleMutation, useCustomerQuery, usePaymentMethodsQuery,
  useProductPagesInfiniteQuery, useSalesQuery,
  type Sale, type SalePaymentStatus, type SaleStatus,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import { pickerPages, usePickerSearch } from '@/lib/pickerPages';
import {
  Alert, Badge, Button, Card, Checkbox, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { CustomerPicker } from '@/components/CustomerPicker';
import { SummaryCards } from '@/components/SummaryCards';
import { DateRangeFilter, exportRangeOf, rangeFromPreset, type DateRange } from '@/components/DateRangeFilter';
import { ExportMenu } from '@/components/ExportMenu';
import { SaleDetailModal } from './SaleDetailModal';

export const SALE_TONE: Record<SaleStatus, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  PENDING: 'warning',
  CONFIRMED: 'brand',
  PARTIALLY_DELIVERED: 'brand',
  DELIVERED: 'brand',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
  EXPIRED: 'neutral',
};

export const PAYMENT_TONE: Record<SalePaymentStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  UNPAID: 'danger',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  OVERPAID: 'warning',
  REFUNDED: 'neutral',
};

interface LineDraft {
  variantId: string;
  quantity: string;
  unitPrice: string;
  discountValue: string;
  /**
   * The catalogue price, taken when the product is chosen: the product list is
   * paged and searched, so the chosen product may no longer be in it.
   */
  listPrice: string;
  /** Stock available when it was chosen; '' for a product that tracks none. */
  available: string;
}

const emptyLine: LineDraft = {
  variantId: '', quantity: '1', unitPrice: '', discountValue: '', listPrice: '', available: '',
};

/**
 * `defaultType` pins the list to one kind of document, which is what makes
 * Quotations a sidebar entry of its own rather than a filter people must find.
 */
export function SalesPage({ defaultType = '' }: { defaultType?: string } = {}) {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [type, setType] = useState(defaultType);
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  // Today by default: a till screen is about today's takings, and a list
  // that opens on every sale ever made buries them.
  const [range, setRange] = useState<DateRange>(rangeFromPreset('today'));
  const exportRange = exportRangeOf(range);

  const { data, isLoading, isError, error, refetch, isFetching } = useSalesQuery({
    page,
    limit: 25,
    search: search || undefined,
    type: type || undefined,
    status: status || undefined,
    includeHidden: showHidden || undefined,
    from: range.from || undefined,
    to: range.to || undefined,
  });

  const [saleType, setSaleType] = useState(defaultType || 'INVOICE');
  // A quotation is only an offer, so it may name goods not yet in stock; anything
  // else can only sell what is on the shelf.
  const sellsStock = saleType !== 'QUOTATION';
  const [productSearch, setProductSearch] = usePickerSearch();
  const { items: products, picker: productPicker } = pickerPages(
    useProductPagesInfiniteQuery({
      status: 'ACTIVE',
      search: productSearch,
      withStock: true,
      inStock: sellsStock || undefined,
    }),
    setProductSearch,
  );
  const { data: methods } = usePaymentMethodsQuery();
  const { data: accounts } = useAccountsQuery({ limit: 50 });
  const [createSale, createState] = useCreateSaleMutation();

  /**
   * Cash or credit is the question the counter actually asks. Cash confirms and
   * takes the money in one go; credit leaves the balance standing against the
   * customer until they pay.
   */
  const [terms, setTerms] = useState<'cash' | 'credit'>('cash');
  const [tender, setTender] = useState({ methodId: '', accountId: '', reference: '' });
  const [customerId, setCustomerId] = useState('');
  // Paid now, taken later: the goods still leave stock, and wait on the
  // To Collect list until they are handed over.
  const [collectLater, setCollectLater] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>([{ ...emptyLine }]);
  const [failure, setFailure] = useState<string | null>(null);

  const variantOptions = products.flatMap((product) =>
    (product.variants ?? []).map((variant) => ({
      id: variant.id,
      label: variant.available == null
        ? `${product.name} — ${variant.sku}`
        : `${product.name} — ${variant.sku} · ${Number(variant.available).toLocaleString()} in stock`,
      price: variant.sellingPrice ?? product.sellingPrice,
      available: variant.available ?? null,
    })),
  );

  const filled = lines.filter((line) => line.variantId && Number(line.quantity) > 0);

  // The same product on two lines draws on the same shelf.
  const wanted = new Map<string, number>();
  for (const line of filled) {
    wanted.set(line.variantId, (wanted.get(line.variantId) ?? 0) + Number(line.quantity));
  }
  const shortOf = (line: LineDraft): boolean =>
    sellsStock && line.available !== '' && (wanted.get(line.variantId) ?? 0) > Number(line.available);
  const short = filled.some(shortOf);

  const setLine = (index: number, patch: Partial<LineDraft>): void => {
    setLines((current) =>
      current.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    );
  };

  const resetForm = (): void => {
    setSaleType('INVOICE');
    setCustomerId('');
    setCollectLater(false);
    setLines([{ ...emptyLine }]);
    setFailure(null);
  };

  /** What the draft will come to, so the counter is not a surprise on save. */
  const estimate = filled.reduce((sum, line) => {
    const price = Number(line.unitPrice !== '' ? line.unitPrice : line.listPrice);
    const gross = price * Number(line.quantity);
    const discount = line.discountValue ? (gross * Number(line.discountValue)) / 100 : 0;
    return sum + gross - discount;
  }, 0);

  // A quotation is an offer: it is never paid, so the question does not arise.
  const cashSale = terms === 'cash' && saleType !== 'QUOTATION';
  const { data: creditCustomer } = useCustomerQuery(customerId, { skip: !customerId });
  // A zero limit means cash only, which is the default for a new customer and
  // the most common reason a credit sale is refused at confirmation.
  // A deposit or a credited return leaves the balance in their favour; that is
  // what store credit can spend.
  const storeCredit = creditCustomer ? Math.max(0, -Number(creditCustomer.balance)) : 0;
  const tenderType = methods?.find((method) => method.id === tender.methodId)?.type;
  const overCredit = tenderType === 'STORE_CREDIT' && estimate > storeCredit;
  const creditRoom =
    creditCustomer && Number(creditCustomer.creditLimit) > 0
      ? Number(creditCustomer.creditLimit) - Number(creditCustomer.balance)
      : null;

  const submit = async (): Promise<void> => {
    setFailure(null);
    try {
      const created = await createSale({
        type: saleType,
        customerId: customerId || undefined,
        ...(collectLater && saleType !== 'QUOTATION' ? { collectLater: true } : {}),
        // A cash sale is confirmed and paid in the same request; a credit sale
        // is left as a draft for someone to confirm.
        ...(cashSale
          ? {
              settlement: {
                methodId: tender.methodId,
                ...(tender.accountId ? { accountId: tender.accountId } : {}),
                ...(tender.reference ? { reference: tender.reference } : {}),
              },
            }
          : {}),
        lines: filled.map((line) => ({
          variantId: line.variantId,
          quantity: Number(line.quantity),
          ...(line.unitPrice === '' ? {} : { unitPrice: Number(line.unitPrice) }),
          ...(line.discountValue === ''
            ? {}
            : { discountType: 'PERCENTAGE', discountValue: Number(line.discountValue) }),
        })),
      }).unwrap();

      setCreating(false);
      resetForm();
      setOpenId(created.id);
    } catch (createError) {
      setFailure(apiErrorMessage(createError, 'That sale could not be created'));
    }
  };

  return (
    <>
      <PageHeader
        title={defaultType === 'QUOTATION' ? 'Quotations' : 'Sales'}
        description={
          defaultType === 'QUOTATION'
            ? 'Offers to customers. A quotation never moves stock — convert it when accepted.'
            : 'Quotations, orders and invoices. Stock and money move when a sale is confirmed.'
        }
        action={
          <div className="flex gap-2">
            <ExportMenu
              period={exportRange.label}
              items={[
                {
                  label: 'Sales listing — every sale, one per line',
                  path: '/reports/sales/listing/export',
                  params: { from: exportRange.from, to: exportRange.to, customerId: customerId || undefined },
                },
              ]}
            />
            <Can permission={P.SALE_CREATE}>
              <Button icon={Plus} onClick={() => setCreating(true)}>New sale</Button>
            </Can>
          </div>
        }
      />

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Sales', value: String(data.summary.count), hint: 'in this period' },
            { label: 'Total', value: formatMoney(String(data.summary.total), format) },
            {
              label: 'Collected',
              value: formatMoney(String(data.summary.collected), format),
              hint: 'money in',
            },
            {
              label: 'Outstanding',
              value: formatMoney(String(data.summary.outstanding), format),
              hint: 'still owed',
              tone: 'owed',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="min-w-48 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Number or customer"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          {defaultType === '' && (
            <div className="w-full sm:w-44">
              <Select
                label="Type"
                value={type}
                onChange={(event) => {
                  setType(event.target.value);
                  setPage(1);
                }}
              >
                <option value="">All types</option>
                <option value="QUOTATION">Quotations</option>
                <option value="ORDER">Orders</option>
                <option value="INVOICE">Invoices</option>
                <option value="POS_SALE">Till sales</option>
              </Select>
            </div>
          )}
          <DateRangeFilter
            value={range}
            onChange={(next) => {
              setRange(next);
              setPage(1);
            }}
          />
          <div className="w-full sm:w-44">
            <Select
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </div>
          <Can permission={P.SALE_DELETE}>
            <div className="w-full sm:w-44">
              <Select
                label="Hidden sales"
                value={showHidden ? 'show' : 'hide'}
                onChange={(event) => {
                  setShowHidden(event.target.value === 'show');
                  setPage(1);
                }}
              >
                <option value="hide">Not shown</option>
                <option value="show">Shown</option>
              </Select>
            </div>
          </Can>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={Receipt}
            title={search || type || status ? 'Nothing matches those filters' : 'No sales yet'}
            description={
              search || type || status
                ? 'Try different filters.'
                : 'Create a sale, or ring one up at the till.'
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Number</Th>
                  <Th>Customer</Th>
                  <Th>Date</Th>
                  <Th numeric>Total</Th>
                  <Th numeric>Due</Th>
                  <Th>Status</Th>
                  <Th>Payment</Th>
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((sale: Sale) => (
                  // The whole row opens the sale: the number was a small target
                  // for a till that may be a touchscreen. It stays a button so
                  // the keyboard reaches it and screen readers announce it.
                  <tr
                    key={sale.id}
                    className={`cursor-pointer hover:bg-sunken ${sale.deletedAt ? 'opacity-60' : ''}`}
                    onClick={() => setOpenId(sale.id)}
                  >
                    <Td>
                      <button
                        type="button"
                        className="font-medium hover:underline"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenId(sale.id);
                        }}
                      >
                        {sale.number}
                      </button>
                      {sale.deletedAt && (
                        <Badge tone="danger" className="ml-2">hidden</Badge>
                      )}
                    </Td>
                    <Td className="text-secondary">{sale.customer?.name ?? 'Walk-in'}</Td>
                    <Td className="text-secondary">{formatDate(sale.issuedAt, format)}</Td>
                    <Td numeric>{formatMoney(sale.total, format)}</Td>
                    <Td
                      numeric
                      className={Number(sale.dueAmount) > 0 ? 'text-red-600' : 'text-secondary'}
                    >
                      {formatMoney(sale.dueAmount, format)}
                    </Td>
                    <Td>
                      <Badge tone={SALE_TONE[sale.status]}>{sale.status.toLowerCase()}</Badge>
                    </Td>
                    <Td>
                      <Badge tone={PAYMENT_TONE[sale.paymentStatus]}>
                        {sale.paymentStatus.toLowerCase().replace(/_/g, ' ')}
                      </Badge>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => {
          setCreating(false);
          resetForm();
        }}
        title="New sale"
        description={cashSale ? "Take payment confirms the sale: the stock leaves and the money is recorded." : "Created as a draft. Nothing leaves stock until you confirm it."}
        size="lg"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setCreating(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void submit()}
              loading={createState.isLoading}
              disabled={
                filled.length === 0
                || short
                || (cashSale && overCredit)
                || (cashSale && !tender.methodId)
                || (terms === 'credit' && saleType !== 'QUOTATION' && !customerId)
              }
            >
              {cashSale ? 'Take payment' : 'Create draft'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {failure && <Alert tone="danger">{failure}</Alert>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Type"
              value={saleType}
              onChange={(event) => setSaleType(event.target.value)}
              hint={
                saleType === 'QUOTATION'
                  ? 'A quotation is an offer — it never moves stock.'
                  : undefined
              }
            >
              <option value="INVOICE">Invoice</option>
              <option value="ORDER">Order</option>
              <option value="QUOTATION">Quotation</option>
            </Select>
            <CustomerPicker
              value={customerId}
              onChange={setCustomerId}
              hint="Leave blank for a walk-in."
            />
          </div>

          {saleType !== 'QUOTATION' && (
            <div className="space-y-3 rounded-lg border border-subtle p-3">
              <Select
                label="How is it paid?"
                value={terms}
                onChange={(event) => setTerms(event.target.value as 'cash' | 'credit')}
              >
                <option value="cash">Cash — paid now</option>
                <option value="credit">Credit — pay later</option>
              </Select>

              {terms === 'cash' ? (
                <div className="grid gap-3 sm:grid-cols-3">
                  <Select
                    label="Method"
                    required
                    value={tender.methodId}
                    onChange={(event) => setTender({ ...tender, methodId: event.target.value })}
                    error={
                      overCredit
                        ? `Only ${formatMoney(String(storeCredit), format)} on account`
                        : undefined
                    }
                  >
                    <option value="">Select a method</option>
                    {methods
                      ?.filter((method) =>
                        method.type !== 'CREDIT'
                        && (method.type !== 'STORE_CREDIT' || storeCredit > 0))
                      .map((method) => (
                        <option key={method.id} value={method.id}>
                          {method.type === 'STORE_CREDIT'
                            ? `${method.name} — ${formatMoney(String(storeCredit), format)} on account`
                            : method.name}
                        </option>
                      ))}
                  </Select>
                  <Select
                    label="Into account"
                    value={tender.accountId}
                    onChange={(event) => setTender({ ...tender, accountId: event.target.value })}
                    hint="Blank uses the method's own account."
                  >
                    <option value="">
                      {methods?.find((m) => m.id === tender.methodId)?.account?.name ?? 'Default'}
                    </option>
                    {accounts?.items
                      .filter((account) => account.isActive)
                      .map((account) => (
                        <option key={account.id} value={account.id}>{account.name}</option>
                      ))}
                  </Select>
                  <Input
                    label="Reference"
                    value={tender.reference}
                    onChange={(event) => setTender({ ...tender, reference: event.target.value })}
                    hint="For a transfer or mobile money."
                  />
                </div>
              ) : !customerId ? (
                <Alert tone="warning">
                  A credit sale needs a customer — someone has to owe the money. Pick one above,
                  or add one without leaving this form.
                </Alert>
              ) : creditRoom === null ? (
                <Alert tone="warning">
                  {creditCustomer?.name} has no credit limit, so they are cash only. Give them a
                  limit on their customer record before selling to them on credit.
                </Alert>
              ) : (
                <Alert tone="info">
                  {creditCustomer?.name} owes {formatMoney(creditCustomer?.balance ?? '0', format)}{' '}
                  of a {formatMoney(creditCustomer?.creditLimit ?? '0', format)} limit
                  {creditRoom < estimate && (
                    <strong> — this sale would take them past it.</strong>
                  )}
                </Alert>
              )}
            </div>
          )}

          {saleType !== 'QUOTATION' && (
            <Checkbox
              label="Collect later — the goods stay here for now"
              description="The customer pays now and comes back for the goods. Stock still goes down now — the goods are theirs — and the sale waits on To Collect until it is handed over."
              checked={collectLater}
              onChange={(event) => setCollectLater(event.target.checked)}
            />
          )}

          <div>
            <p className="mb-2 text-sm font-medium">Lines</p>
            <div className="space-y-3">
              {lines.map((line, index) => (
                <div key={index} className="flex flex-wrap items-end gap-3">
                  <div className="min-w-44 flex-1">
                    <Select
                      label={index === 0 ? 'Product' : undefined}
                      value={line.variantId}
                      onChange={(event) => {
                        const option = variantOptions.find((entry) => entry.id === event.target.value);
                        setLine(index, {
                          variantId: event.target.value,
                          listPrice: option?.price != null ? String(option.price) : '',
                          available: option?.available ?? '',
                        });
                      }}
                      {...productPicker}
                    >
                      <option value="">Select a product</option>
                      {variantOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </Select>
                  </div>
                  <div className="w-24">
                    <Input
                      label={index === 0 ? 'Qty' : undefined}
                      type="number"
                      min="0"
                      step="0.0001"
                      value={line.quantity}
                      onChange={(event) => setLine(index, { quantity: event.target.value })}
                      error={shortOf(line) ? `Only ${Number(line.available).toLocaleString()}` : undefined}
                      hint={
                        line.variantId && line.available !== '' && !shortOf(line)
                          ? `${Number(line.available).toLocaleString()} in stock`
                          : undefined
                      }
                    />
                  </div>
                  <div className="w-28">
                    <Input
                      label={index === 0 ? 'Price' : undefined}
                      type="number"
                      min="0"
                      step="0.0001"
                      placeholder={line.listPrice}
                      value={line.unitPrice}
                      onChange={(event) => setLine(index, { unitPrice: event.target.value })}
                    />
                  </div>
                  <Can permission={P.SALE_DISCOUNT}>
                    <div className="w-24">
                      <Input
                        label={index === 0 ? 'Disc %' : undefined}
                        type="number"
                        min="0"
                        max="100"
                        value={line.discountValue}
                        onChange={(event) => setLine(index, { discountValue: event.target.value })}
                      />
                    </div>
                  </Can>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={lines.length === 1}
                    onClick={() =>
                      setLines((current) => current.filter((_, position) => position !== index))
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>

            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => setLines((current) => [...current, { ...emptyLine }])}
            >
              Add line
            </Button>
          </div>

          <div className="flex justify-end border-t border-subtle pt-3">
            <p className="text-sm text-secondary">
              Estimated total{' '}
              <span className="font-semibold text-primary">
                {formatMoney(estimate.toFixed(2), format)}
              </span>
              <span className="ml-2 text-xs text-muted">tax and rounding applied on save</span>
            </p>
          </div>
        </div>
      </Modal>

      {openId && <SaleDetailModal saleId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
